/**
 * Sync Alberta tire recycling fee line item when cart contains product_type Tire.
 * Runs on cart updates and before checkout (with winter-package-checkout-guard).
 */
(function () {
  const TIRE_TYPE = 'Tire';
  const AUTO_PROP = '_auto';
  const AUTO_VALUE = 'tire_recycling_fee';
  const FORM_IDS = ['cart', 'CartDrawer-Form', 'cart-notification-form'];
  const CHECKOUT_FORMS = FORM_IDS;

  let syncPromise = null;

  const FEE_PRODUCT_HANDLE = 'alberta-tire-recycling-fee';
  let resolvedConfigPromise = null;

  function readConfig() {
    const el = document.getElementById('tire-recycling-fee-config');
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Use Liquid config, or fetch variant from published fee product when metafield is unset. */
  function resolveConfig() {
    if (resolvedConfigPromise) return resolvedConfigPromise;

    const base = readConfig();
    if (!base || !base.enabled) {
      resolvedConfigPromise = Promise.resolve(null);
      return resolvedConfigPromise;
    }
    const variantId = String(base.variantId || '').trim();
    if (variantId) {
      resolvedConfigPromise = Promise.resolve(base);
      return resolvedConfigPromise;
    }

    resolvedConfigPromise = fetch('/products/' + FEE_PRODUCT_HANDLE + '.json', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        const vid = data && data.product && data.product.variants && data.product.variants[0];
        if (!vid || !vid.id) return null;
        return Object.assign({}, base, { variantId: String(vid.id) });
      })
      .catch(function () {
        return null;
      });

    return resolvedConfigPromise;
  }

  function getCheckoutUrl() {
    return (window.routes && window.routes.checkout_url) || '/checkout';
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  function cartChange(body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('change');
      return r.json();
    });
  }

  function cartAdd(body) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('add');
      return r.json();
    });
  }

  function isAutoFeeLine(item, feeVariantId) {
    if (!item || feeVariantId == null) return false;
    if (item.variant_id === feeVariantId) return true;
    const props = item.properties || {};
    return props[AUTO_PROP] === AUTO_VALUE;
  }

  function tireQuantity(cart, feeVariantId) {
    let qty = 0;
    (cart.items || []).forEach(function (item) {
      if (isAutoFeeLine(item, feeVariantId)) return;
      const pt = (item.product_type || '').trim();
      if (pt === TIRE_TYPE) qty += item.quantity || 0;
    });
    return qty;
  }

  function feeLineInfo(cart, feeVariantId) {
    let lines = [];
    let qty = 0;
    (cart.items || []).forEach(function (item, idx) {
      if (isAutoFeeLine(item, feeVariantId)) {
        lines.push(idx + 1);
        qty += item.quantity || 0;
      }
    });
    return { line: lines.length > 0 ? lines[0] : null, qty: qty, duplicates: lines.slice(1) };
  }

  function reloadCartSections() {
    const cartItems =
      document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      return cartItems.onCartUpdate();
    }
    return Promise.resolve();
  }

  function syncRecyclingFee() {
    if (syncPromise) return syncPromise;

    syncPromise = resolveConfig()
      .then(function (config) {
        if (!config || !config.enabled || !config.variantId) return null;

        const feeVariantId = parseInt(String(config.variantId), 10);
        if (!Number.isFinite(feeVariantId) || feeVariantId <= 0) return null;

        return fetchCart().then(function (cart) {
          const tireQty = tireQuantity(cart, feeVariantId);
          const feeInfo = feeLineInfo(cart, feeVariantId);

          // Remove duplicate fee lines first (race condition cleanup)
          var removeDuplicates = Promise.resolve();
          if (feeInfo.duplicates && feeInfo.duplicates.length > 0) {
            removeDuplicates = feeInfo.duplicates.reduce(function (chain, lineNum) {
              return chain.then(function () { return cartChange({ line: lineNum, quantity: 0 }); });
            }, Promise.resolve());
          }

          return removeDuplicates.then(function () {
            if (tireQty === 0) {
              if (feeInfo.line != null) {
                return cartChange({ line: feeInfo.line, quantity: 0 }).then(reloadCartSections);
              }
              return cart;
            }

            if (feeInfo.qty === tireQty && feeInfo.duplicates.length === 0) return cart;

            if (feeInfo.line == null) {
              return cartAdd({
                items: [
                  {
                    id: feeVariantId,
                    quantity: tireQty,
                    properties: { _auto: AUTO_VALUE },
                  },
                ],
              }).then(reloadCartSections);
            }

            return cartChange({ line: feeInfo.line, quantity: tireQty }).then(reloadCartSections);
          });
        });
      })
      .finally(function () {
        syncPromise = null;
      });

    return syncPromise;
  }

  window.syncTireRecyclingFee = syncRecyclingFee;

  function onReady() {
    syncRecyclingFee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      syncRecyclingFee();
    });
  }

  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (CHECKOUT_FORMS.indexOf(form.id) === -1) return;
      const sub = e.submitter;
      if (!sub || sub.getAttribute('name') !== 'checkout') return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      resolveConfig().then(function (config) {
        if (!config || !config.enabled || !config.variantId) {
          window.location.href = getCheckoutUrl();
          return;
        }
        syncRecyclingFee()
          .then(function () {
            window.location.href = getCheckoutUrl();
          })
          .catch(function (err) {
            console.error(err);
            window.location.href = getCheckoutUrl();
          });
      });
    },
    true
  );
})();
